import React from "react";
import { TransformationContext, defaultTransformation, Transformation } from "./TransformationContext";
import { useRenderTarget, RenderTarget } from "./renderTarget";
import { createPDFStyleSheet, parseTransformForPDF } from "./cssHelpers";
import { isUiModeDeveloper } from "../../uiMode";
import PdfListUtils from "../PdfLists/general/PdfListUtils";
import { getPdfRenderer } from "../../getPdfRenderer";

// Farb-Konvertierung: rgb()/rgba() -> #RRGGBB
export function colorToHex(color: string): string {
  const m = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!m) return color; // falls schon hex oder unbekanntes Format
  const r = parseInt(m[1], 10);
  const g = parseInt(m[2], 10);
  const b = parseInt(m[3], 10);
  return "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
}

// Ersetzt alle Farbwerte in einem Style-Objekt
export function convertColorsInStyle(style: any) {
  if (!style) return style;
  for (const key of Object.keys(style)) {
    if (typeof style[key] === "string" && /rgba?\(/.test(style[key])) {
      style[key] = colorToHex(style[key]);
    }
  }
}

// Erweiterte Style-Properties für PDF-spezifische Properties
export type RDivStyle = React.CSSProperties & {
  paddingHorizontal?: number | string;
  paddingVertical?: number | string;
  marginHorizontal?: number | string;
  marginVertical?: number | string;
};

export type RDivProps = React.HTMLAttributes<HTMLDivElement> & {
  style?: RDivStyle;
  children?: React.ReactNode;
};

export const RDiv = React.forwardRef<HTMLDivElement, RDivProps>((props, ref) => {
  const target = useRenderTarget();
  const parentTransformation = React.useContext(TransformationContext);

  if (target === RenderTarget.web) {
    const { style, children, ...rest } = props as any;

    // Für Web: paddingHorizontal/paddingVertical in normale CSS umwandeln
    let webStyle = { ...style };
    if (style?.paddingHorizontal !== undefined) {
      webStyle.paddingLeft = style.paddingHorizontal;
      webStyle.paddingRight = style.paddingHorizontal;
      delete webStyle.paddingHorizontal;
    }
    if (style?.paddingVertical !== undefined) {
      webStyle.paddingTop = style.paddingVertical;
      webStyle.paddingBottom = style.paddingVertical;
      delete webStyle.paddingVertical;
    }
    if (style?.marginHorizontal !== undefined) {
      webStyle.marginLeft = style.marginHorizontal;
      webStyle.marginRight = style.marginHorizontal;
      delete webStyle.marginHorizontal;
    }
    if (style?.marginVertical !== undefined) {
      webStyle.marginTop = style.marginVertical;
      webStyle.marginBottom = style.marginVertical;
      delete webStyle.marginVertical;
    }

    return (
      <div ref={ref} {...rest} style={webStyle}>
        {children}
      </div>
    );
  }

  //
  // Für PDF: Transform-Properties parsen und in PDF-kompatible Properties umwandeln
  //

  const style = props.style ?? {};
  const transformParsed = parseTransformForPDF(style.transform, style.width, style.height);

  // Fehler ausgeben (nur in Development)
  if (isUiModeDeveloper() && transformParsed.transform) {
    console.error(
      `RDiv: Transform '${transformParsed.transform}' kann nicht in PDF konvertiert werden. Nur translate() und scale() werden unterstützt.`,
    );
  }

  PdfListUtils.convertStyleUnitsForPDF(style, "RDiv"); // no itx
  PdfListUtils.applyTransformation(style, parentTransformation);
  convertColorsInStyle(style);

  // PDF-StyleSheet erstellen
  let pdfStyle = createPDFStyleSheet(style);

  //
  // BEACHTE !!!!!!
  //
  // Die transformation wird NUR!! auf die nächste Ebene angewendet
  //
  // Das bedeutet:
  //  1. die nächste Ebene muss position="absolut" haben UND
  //     left und top als css Property haben!!!
  //
  // das dieses Div ja dann an seinem transformierten punkt sitzt
  // übergeben wir für tiefere Eben wirder KEINE transformation.

  // Das ist falsch für die Scale.
  // Die Scale müss erhalten bleiben da auch tiefere Ebenen skaliert werden müssen,
  // da ein verschteltes RDiv diese Scalierung ja nicht "in sich trägt",
  // sondern eben nur die Verschiebung
  //

  let transformationValue: Transformation | undefined = undefined;

  if (parentTransformation && parentTransformation.scale !== 1) {
    transformationValue = { scale: parentTransformation.scale, translate: { x: 0, y: 0 } };
  }

  if (style.transform) {
    // Transformation aus transform extrahieren und mit parentTransformation kombinieren
    let scale = 1;
    let translate = { x: 0, y: 0 };

    if (parentTransformation) {
      console.log("RDiv: Parent Transformation. Need to multiply ", parentTransformation);
    }

    const scaleMatch = /scale\(([-\d.]+)\)/.exec(style.transform);
    if (scaleMatch) {
      scale = parseFloat(scaleMatch[1]);
    }

    const translateMatch = /translate\((.*?),(.*?)\)/.exec(style.transform);
    if (translateMatch) {
      translate.x = parseFloat(translateMatch[1].trim());
      translate.y = parseFloat(translateMatch[2].trim());
    }

    transformationValue = { scale, translate };
    console.log("RDiv: Set Transformation", transformationValue);
  }

  const pdf = getPdfRenderer();
  if (!pdf) return null;

  const { View } = pdf;

  return (
    <TransformationContext.Provider value={transformationValue}>
      <View style={pdfStyle}>{props.children}</View>
    </TransformationContext.Provider>
  );
});
